import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, LogOut, Eye, EyeOff, CalendarDays, Inbox, BookOpenCheck, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { OwnerCalendar } from '../components/owner/OwnerCalendar';
import { OwnerLeads } from '../components/owner/OwnerLeads';
import { OwnerBookings } from '../components/owner/OwnerBookings';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  ['calendar', 'Availability', CalendarDays],
  ['leads', 'Leads', Inbox],
  ['bookings', 'Bookings', BookOpenCheck]
];

export default function OwnerPortalPage() {
  const [owner, setOwner] = useState(null); // null = checking, false = logged out
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('calendar');

  const api = useMemo(() => {
    const token = localStorage.getItem('wvc_owner_token');
    return axios.create({
      baseURL: API_URL,
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  }, [owner]);

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem('wvc_owner_token');
    try {
      const res = await axios.get(`${API_URL}/owner/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setOwner(res.data.owner);
    } catch {
      localStorage.removeItem('wvc_owner_token');
      setOwner(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Enter your email and password');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/owner/auth/login`, form, { withCredentials: true });
      localStorage.setItem('wvc_owner_token', res.data.token);
      setOwner(res.data.owner);
      toast.success(`Welcome back${res.data.owner.name ? ', ' + res.data.owner.name : ''}!`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('wvc_owner_token');
    try {
      await axios.post(`${API_URL}/owner/auth/logout`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch { /* noop */ }
    localStorage.removeItem('wvc_owner_token');
    setOwner(false);
    toast.success('Logged out');
  };

  if (owner === null) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#1A3626]/20 border-t-[#1A3626] rounded-full animate-spin" />
      </div>
    );
  }

  if (owner === false) {
    return (
      <div className="min-h-screen bg-[#1A3626] flex items-center justify-center px-4 font-wvc-body">
        <div className="max-w-md w-full">
          <div className="bg-[#FDFCF8] rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#1A3626] rounded-full mb-4">
                <Building2 className="w-7 h-7 text-[#C6A87C]" />
              </div>
              <h1 className="font-wvc-display text-3xl text-[#0A1710]">Venue Owner Portal</h1>
              <p className="text-sm text-[#1A3626]/60 mt-2">Manage your venue's availability, leads and bookings.</p>
            </div>
            <form onSubmit={login} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#1A3626]/70 mb-1 block">Email</label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@yourvenue.com" className="h-12" data-testid="owner-email-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#1A3626]/70 mb-1 block">Password</label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Your password" className="h-12 pr-10" data-testid="owner-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-12 bg-[#1A3626] hover:bg-[#254834] rounded-full" data-testid="owner-login-btn">
                {busy ? 'Signing in…' : 'Sign in to your venue'}
              </Button>
            </form>
            <p className="text-[11px] text-[#1A3626]/40 text-center mt-6">Access is provided by the WVC team. Contact us if you need credentials.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3EFEB] font-wvc-body" data-testid="owner-dashboard">
      <header className="bg-[#1A3626] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-[0.3em] text-[#C6A87C] font-bold">Venue Owner Portal</p>
            <h1 className="font-wvc-display text-xl md:text-2xl text-[#FDFCF8] truncate" data-testid="owner-venue-name">{owner.venue_name}</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a href={`/venues/${owner.venue_slug}`} target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 text-[#FDFCF8]/70 hover:text-[#C6A87C] text-xs uppercase tracking-wider font-semibold px-3 py-2 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> View page
            </a>
            <button onClick={logout} className="flex items-center gap-1.5 border border-[#C6A87C]/40 text-[#C6A87C] hover:bg-[#C6A87C] hover:text-[#0A1710] text-xs uppercase tracking-wider font-semibold px-4 py-2 rounded-full transition-colors" data-testid="owner-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-2 flex-wrap mb-6">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] font-bold transition-colors ${tab === key ? 'bg-[#1A3626] text-[#FDFCF8]' : 'bg-white border border-[#E5DCCF] text-[#1A3626]/70 hover:border-[#C6A87C]'}`}
              data-testid={`owner-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === 'calendar' && <OwnerCalendar api={api} />}
        {tab === 'leads' && <OwnerLeads api={api} />}
        {tab === 'bookings' && <OwnerBookings api={api} />}
      </main>
    </div>
  );
}
